import { isNil, isNotNil } from '@flowio/is';
import postPaymentMethodData from '../../services/paymentRequest/postPaymentMethodData';
import type { DomReference, PaymentButtons } from '../../types';
import injectCommonCss, { elementClasses } from '../../utilities/commonStyles';
import {
  createElement, createPaymentButton, removeElement, resolveButton, resolveElement,
} from '../../utilities/elementHelpers';
import { elIds, uiClasses } from './ui-constants';
import injectSelectPaymentOptionCss from './ui-css';
import isInternalPaymentRequest from '../../validators/isInternalPaymentRequest';

const selectFieldName = 'select_payment_option';

type HandleSelectIssuerActionParams = {
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  action: io.flow.v0.models.ActionSelectPaymentOption,
  container: DomReference,
  buttons?: PaymentButtons,
};

function isSelectIssuerAction(
  action: io.flow.v0.unions.Action,
): action is io.flow.v0.models.ActionSelectPaymentOption {
  return action.type === 'select_payment_option';
}

// Take the language section of the locale and render text by language
// In case no language, default to dutch for iDEAL
function getButtonText(
  action: io.flow.v0.models.ActionSelectPaymentOption,
  locale?: String,
): string {
  const language = locale?.split('-')[0];
  switch (language) {
    case 'nl':
      return 'Doorgaan';
    case 'de':
      return 'Weiter';
    case 'en':
      return 'Continue';
    default:
      switch (action.option_type) {
        case 'ideal_issuer_option':
          return 'Doorgaan';
        default:
          return 'Continue';
      }
  }
}

// Take the language section of the locale and render text by language and payment method flow
// In case no language, default to dutch for iDEAL
function getInstructionalText(
  action: io.flow.v0.models.ActionSelectPaymentOption,
  locale?: string,
): string {
  const language: string | undefined = locale?.split('-')[0];
  switch (action.option_type) {
    case 'ideal_issuer_option':
      switch (language) {
        case 'nl':
          return 'Selecteer uw bank';
        case 'de':
          return 'Wählen Sie Ihre Bank aus';
        case 'en':
          return 'Select your bank';
        default:
          return 'Selecteer uw bank';
      }
    default:
      switch (language) {
        case 'nl':
          return 'Selecteer een betaalmethoden';
        case 'de':
          return 'Wählen Sie Ihre Zahlungsmethode';
        default:
          return 'Select an option';
      }
  }
}

function createSelectIssuerUi({
  container,
  action,
  locale,
}: {
  container: HTMLElement,
  action: io.flow.v0.models.ActionSelectPaymentOption,
  locale?: string,
}): void {
  const instructionalHeader = createElement('h2', {
    id: elIds.hdr_instruct,
  });
  instructionalHeader.innerHTML = getInstructionalText(action, locale);
  container.appendChild(instructionalHeader);

  const innerContainer = createElement('div', {
    id: elIds.container_id,
  });

  action.options.forEach((option, i) => {
    const issuerDiv = createElement('div', {
      class: uiClasses.optionButton,
    });
    const svgLogo = option.logo && option.logo.find((logo) => logo.type === 'svg')?.url;
    issuerDiv.innerHTML = `<div class="${uiClasses.radioContainer} ${elementClasses.radio}">
        <input type="radio" id="radio-button.${i}" data-index="${i}" class="${uiClasses.radio}" name="${selectFieldName}" value="${option.id}"${i === 0 ? ' checked' : ''} />
        <label class="${uiClasses.radioLabel}" for="radio-button.${i}">
          <span class="${uiClasses.optionName}">${option.name}</span>
          ${svgLogo ? `<span class="${uiClasses.optionImg}"><img src="${svgLogo}" alt="${option.name}" height="30" /></span>` : ''}
        </label>
      </div>`;
    innerContainer.appendChild(issuerDiv);
  });

  container.appendChild(innerContainer);
}

type CreateSelectIssuerButtonParams = {
  text: string,
  buttonEl: HTMLElement,
  clickCallback: (issuerId: string) => void,
};

function createSelectPaymentOptionButton({
  text,
  buttonEl,
  clickCallback,
}: CreateSelectIssuerButtonParams): void {
  const continueButton = createPaymentButton(
    buttonEl,
    {
      text,
      classNames: [uiClasses.optionButton],
    },
  );
  continueButton.addEventListener('click', () => {
    const selectedRadio = document.querySelector<HTMLInputElement>(`input[name=${selectFieldName}]:checked`);
    if (isNil(selectedRadio)) {
      throw new Error('handleSelectPaymentOptionAction: selected iDeal issuer could not be detected.');
    }
    clickCallback(selectedRadio.value);
  });
}

export function removeSelectIssuerButton(container: HTMLElement): void {
  const button = container.querySelector<HTMLElement>(`.${uiClasses.optionButton}`);
  if (isNotNil(button)) {
    removeElement(button);
  }
}

export default function handleSelectPaymentOptionAction(
  params: HandleSelectIssuerActionParams,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  const { pr, action } = params;
  if (!isSelectIssuerAction(action)) {
    throw new Error('handleSelectPaymentOptionAction: incorrect \'action\' parameter type.');
  }

  const resolvedContainer = resolveElement(params.container);
  const resolvedButton = resolveButton(params.buttons, 'primary');
  // const resolvedButton = resolveButton(params.buttons, 'primary');

  if (isNil(resolvedContainer)) {
    // eslint-disable-next-line no-console
    console.error('handleSelectPaymentOptionAction: Mounting container was not set.');
    return Promise.reject(new Error('handleSelectPaymentOptionAction: Mounting container was not set.'));
  }

  const locale = isInternalPaymentRequest(pr) ? pr.payment_request.locale : pr.locale;

  return new Promise((resolve, reject) => {
    injectSelectPaymentOptionCss();
    injectCommonCss(['radio', 'paymentButton']);
    createSelectIssuerUi({
      container: resolvedContainer,
      action,
      locale,
    });
    createSelectPaymentOptionButton({
      text: getButtonText(action, locale),
      buttonEl: resolvedButton,
      clickCallback: (issuerId) => {
        removeSelectIssuerButton(resolvedButton);
        postPaymentMethodData(params.pr, {
          type: 'select_option',
          option_id: issuerId,
        }).then((resolvedPr) => {
          resolve(resolvedPr);
        }).catch((error: unknown) => {
          reject(new Error(`handleSelectPaymentOptionAction: exception raised while trying to init iDeal: ${String(error)}`));
        });
      },
    });
  });
}
